import _orderBy from 'lodash/orderBy';

/**
 * @typedef {GetTextDialFilterDataInput}
 * @property {object[]} data an array of data objects
 * @property {FilterConfigs<D>} filterConfigs a map of filter predicate functions
 * @property {string[]} defaultFilterOrder an array of filter predicate keys. The first key that
 * matches to a filterPredicate which contains at least one element will be returned as the
 * defaultFilter.
 * @property {boolean} truncateChartData whether to filter out small values and slice the data to
 * a max length
 */
interface GetTextDialFilterDataInput<D> {
    data: D[];
    defaultFilterOrder: string[];
    filterConfigs: FilterConfigs<D>;
    truncateChartData: boolean;
}

/**
 * Returns the data necessary for setting up a TextDial component as a filter for an array of
 * objects.
 # @param input {GetTextDialFilterDataInput}
 * @returns {TextDialFilterData}
 */
export function getTextDialFilterData<D extends Record<string, unknown>>({
    data,
    filterConfigs,
    defaultFilterOrder,
}: GetTextDialFilterDataInput<D>): TextDialFilterData<D> {
    // In other places, we are getting truncateChartData from a feature flag. Here, we are leaving
    // it set to `true` as a failsafe against wrong data in the API response.
    const truncateChartData = true;

    const result: TextDialFilterData<D> = {
        defaultFilter: '',
        filteredData: {},
    };

    for (const filterKey in filterConfigs) {
        if (Object.prototype.hasOwnProperty.call(filterConfigs, filterKey)) {
            const filterOption = filterConfigs[filterKey];
            const {
                filterPredicate,
                sort,
                smallValueFilter,
                maxSize = 0,
            } = filterOption;

            let mutatedData = [...data];
            let didFilterSmallValues = false;

            if (sort) {
                const { key, order } = sort;

                mutatedData = _orderBy(mutatedData, [key], [order]);
            }

            if (smallValueFilter !== undefined) {
                let smallValueFilterPredicate = (item: D): boolean =>
                    item !== undefined;

                if (typeof smallValueFilter === 'function') {
                    smallValueFilterPredicate = smallValueFilter;
                } else {
                    const { key, min } = smallValueFilter;

                    smallValueFilterPredicate = (item: D): boolean => {
                        const value = item?.[key] ?? null;

                        if (typeof value !== 'number') {
                            return true;
                        }

                        return value >= min;
                    };
                }

                mutatedData = mutatedData.filter((item: D): boolean => {
                    const passesFilterPredicate = filterPredicate(item);

                    if (!truncateChartData) {
                        return passesFilterPredicate;
                    }

                    const passesSmallValueFilter =
                        smallValueFilterPredicate(item);

                    if (
                        smallValueFilter !== undefined &&
                        !passesSmallValueFilter
                    ) {
                        didFilterSmallValues = true;
                    }

                    return passesFilterPredicate && passesSmallValueFilter;
                });
            } else {
                mutatedData = data.filter(filterPredicate);
            }

            if (truncateChartData && maxSize > 0) {
                mutatedData = mutatedData.slice(0, maxSize);
            }

            result.filteredData[filterKey] = {
                data: mutatedData,
                didFilterSmallValues,
            };
        }
    }

    const calculatedDefaultFilter = defaultFilterOrder.reduce(
        (acc: Maybe<string>, filterKey: string): Maybe<string> => {
            if (
                result.filteredData[filterKey].data.length > 0 &&
                acc === null
            ) {
                return filterKey;
            }

            return acc;
        },
        null,
    );

    if (calculatedDefaultFilter !== null) {
        result.defaultFilter = calculatedDefaultFilter;
    } else {
        result.defaultFilter = defaultFilterOrder?.[0] ?? null;
    }

    return result;
}
